import React, { useState, useEffect } from "react";
import Select from "react-select";
import { Button } from "@opendate-app/setlist";
import { useSeriesEditContext } from "../SeriesEditApp";
import DetailImageUpload from "../../../shared/DetailImageUpload";
import darkSelectStyles from "../../../shared/darkSelectStyles";
import { getAspectRatioValue } from "../../../shared/websiteImageRatios";
import StepFooter from "./StepFooter";
import SeriesActivitiesCard from "./SeriesActivitiesCard";
import { PublishedLockNote } from "./PublishedLock";
import { withTaxesUnpicked } from "../scheduleBuilder";

const findOption = (opts, value) => opts.find((o) => String(o.value) === String(value)) || null;

const seriesSelectStyles = {
  ...darkSelectStyles,
  control: (base, state) => ({ ...darkSelectStyles.control(base, state), minHeight: "43px" }),
};

const selectProps = {
  styles: seriesSelectStyles,
  classNamePrefix: "sl-select",
  menuPortalTarget: typeof document !== "undefined" ? document.body : null,
  isClearable: true,
};

const SeriesDetailsSection = () => {
  const { form, setField, options, photoFile, setPhotoFile, setRemovePhoto, removePhoto, goToStep,
    setupWizardEnabled, seriesSettingsEnabled, setTickets, canApplyDetailsToEvents, openApplyDetails, saving, settings, setSettings,
    selectedVenue, lockedPublished } = useSeriesEditContext();
  const ageOptions = options.ageRestrictions.map((a) => ({ value: a, label: a }));
  const venueOptions = options.venues || [];
  const roomOptions = ((selectedVenue && selectedVenue.rooms) || []).map((room) => ({ value: room.id, label: room.name }));

  const [previewUrl, setPreviewUrl] = useState(null);
  useEffect(() => {
    if (!photoFile) { setPreviewUrl(null); return undefined; }
    const url = URL.createObjectURL(photoFile);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [photoFile]);
  const displayedImageUrl = removePhoto ? null : (previewUrl || form.imageUrl);

  return (
    <div className="series-edit-card">
      <h2 className="series-edit-section-title">Series Details</h2>
      <p className="series-edit-section-subtitle">Basic information shared across all events in this series.</p>
      {lockedPublished && <PublishedLockNote message="This series is published, so its schedule, tickets and settings stay as published. To change a date, open it from the series page's events table, or add dates with Add Dates." />}

      <label className="series-edit-label">Title <span className="series-edit-required">*</span></label>
      <input name="title" className="series-edit-input" placeholder="e.g., Summer Concert Series"
             value={form.name || ""} onChange={(e) => setField("name", e.target.value)} />

      <label className="series-edit-label">Subtitle</label>
      <input name="subtitle" className="series-edit-input" placeholder="e.g., Every Friday night on the lawn"
             value={form.subtitle || ""} onChange={(e) => setField("subtitle", e.target.value)} />

      <div className="series-edit-row">
        <div className="series-edit-col">
          <label className="series-edit-label">Type <span className="series-edit-required">*</span></label>
          <Select
            {...selectProps}
            inputId="series-type"
            options={options.eventTypes}
            value={findOption(options.eventTypes, form.eventTypeTagId)}
            onChange={(opt) => setField("eventTypeTagId", opt ? opt.value : "")}
            placeholder="Choose a type…"
          />
        </div>
        <div className="series-edit-col">
          <label className="series-edit-label">Venue <span className="series-edit-required">*</span></label>
          {setupWizardEnabled ? (
            <Select
              {...selectProps}
              inputId="series-venue"
              options={venueOptions}
              value={findOption(venueOptions, form.venueOwnershipId)}
              onChange={(opt) => {
                setField("venueOwnershipId", opt ? opt.value : "");
                setField("venueName", opt ? opt.label : "");
                if (opt?.timeZone) setField("venueTimeZone", opt.timeZone);
                setTickets((current) => ({
                  ...(opt?.value === form.venueOwnershipId ? current : withTaxesUnpicked(current)),
                  capacity: opt?.capacity == null ? "" : String(opt.capacity),
                }));
                if (opt?.value !== form.venueOwnershipId) setSettings((current) => ({ ...current, roomIds: [] }));
              }}
              placeholder="Select venue…"
            />
          ) : (
            <input name="venue" className="series-edit-input" placeholder="Select venue…"
                   value={form.venueName || ""} readOnly />
          )}
        </div>
      </div>

      <label className="series-edit-label">Description</label>
      <textarea name="description" className="series-edit-input series-edit-textarea"
                placeholder="Describe this series for consumers…" value={form.description || ""}
                onChange={(e) => setField("description", e.target.value)} />

      <label className="series-edit-label">Age Restriction</label>
      <Select
        {...selectProps}
        inputId="series-age"
        options={ageOptions}
        value={findOption(ageOptions, form.ageRestriction)}
        onChange={(opt) => setField("ageRestriction", opt ? opt.value : "")}
        placeholder="Choose…"
      />

      {seriesSettingsEnabled && (
        <div className="series-edit-row">
          <div className="series-edit-col">
            <label className="series-edit-label" htmlFor="series-rooms">Rooms</label>
            <Select
              {...selectProps}
              isMulti
              inputId="series-rooms"
              options={roomOptions}
              value={roomOptions.filter((option) => settings.roomIds.includes(option.value))}
              onChange={(selected) => setSettings((current) => ({ ...current, roomIds: (selected || []).map((option) => option.value) }))}
              placeholder="All rooms at the venue"
            />
          </div>
          <div className="series-edit-col">
            <label className="series-edit-label" htmlFor="series-presenter">Presenter</label>
            <input
              id="series-presenter"
              name="presenter"
              className="series-edit-input"
              maxLength={255}
              placeholder="e.g., Presented by Wild Birds Unlimited"
              value={settings.presenter}
              onChange={(e) => setSettings((current) => ({ ...current, presenter: e.target.value }))}
            />
          </div>
        </div>
      )}

      <label className="series-edit-label">Series Photo</label>
      <DetailImageUpload
        imageUrl={displayedImageUrl}
        onUploadFile={(file) => { setPhotoFile(file); setRemovePhoto(false); }}
        onClearImage={() => { setPhotoFile(null); setRemovePhoto(true); }}
        aspectRatio={getAspectRatioValue(form.websiteImageRatio)}
      />
      <p className="series-edit-hint">
        This image appears as the hero banner on the series page and in series cards.
      </p>

      <label className="series-edit-label">Event Template</label>
      <Select
        {...selectProps}
        inputId="series-template"
        options={options.eventTemplates}
        value={findOption(options.eventTemplates, form.eventTemplateId)}
        onChange={(opt) => setField("eventTemplateId", opt ? opt.value : "")}
        placeholder="Choose a template…"
      />

      {canApplyDetailsToEvents && (
        <div className="series-apply-details">
          <div>
            <span className="series-edit-label">Events in this series</span>
            <p className="series-edit-hint">
              Copy this series&apos; type, age restriction, photo and template onto every event in it, for example after
              adding shows.
            </p>
          </div>
          <Button variant="secondary" onClick={openApplyDetails} disabled={saving}>
            Apply series details to all events
          </Button>
        </div>
      )}

      {setupWizardEnabled && <SeriesActivitiesCard />}

      <StepFooter onNext={() => goToStep(setupWizardEnabled ? "schedule" : "display")} />
    </div>
  );
};

export default SeriesDetailsSection;
